import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { Venue } from "@/db/schema";
import { useTRPC } from "@/integrations/trpc/react";

import { EditableImage } from "../base/editable-image";

export type VenueHeaderProps = Pick<
  Venue,
  "id" | "name" | "city" | "headerImageSource" | "thumbnailImageSource"
> & {
  canUpdate: boolean;
  onSaved?: () => void;
};

export function VenueHeader({
  id,
  name,
  city,
  headerImageSource,
  thumbnailImageSource,
  canUpdate,
  onSaved,
}: VenueHeaderProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const queryOpts = trpc.venues.get.queryOptions({ id });

  const { mutate: updateVenue } = useMutation({
    ...trpc.venues.updateContent.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryOpts.queryKey });
      onSaved?.();
    },
  });

  return (
    <EditableImage
      editable={canUpdate}
      className="h-[25svh] w-full object-cover md:h-72 lg:h-86"
      alt={`${name}, ${city}`}
      source={headerImageSource ?? thumbnailImageSource ?? ""}
      bucket="venues"
      prefix={`${id}/headers`}
      onUploadSuccess={(source) => {
        queryClient.setQueryData(queryOpts.queryKey, (data) => {
          if (!data) return data;
          return { ...data, headerImageSource: source };
        });
      }}
      onDiscard={(original) => {
        queryClient.setQueryData(queryOpts.queryKey, (data) => {
          if (!data) return data;
          return { ...data, headerImageSource: original };
        });
      }}
      onSave={(source) => {
        updateVenue({
          id,
          headerImageSource: source,
        });
      }}
    />
  );
}
